import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Shirt, ExternalLink, Sparkles, Loader2, Ruler, Download, Heart, Flame, MessageSquare, Send, BellRing } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { API } from "@/lib/api";
import axios from "axios";

export default function SharedOutfit() {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(undefined);
  const [likes, setLikes] = useState(0);
  const [liked, setLiked] = useState(false);
  const [comments, setComments] = useState([]);
  const [cAuthor, setCAuthor] = useState("");
  const [cText, setCText] = useState("");
  const [sending, setSending] = useState(false);
  const [myIds, setMyIds] = useState([]);
  const [seenReplies, setSeenReplies] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/public/outfits/${shareId}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [shareId]);

  useEffect(() => {
    try { setMyIds(JSON.parse(localStorage.getItem(`mylook_mine_${shareId}`) || "[]")); } catch { setMyIds([]); }
    try { setSeenReplies(JSON.parse(localStorage.getItem(`mylook_seenreplies_${shareId}`) || "[]")); } catch { setSeenReplies([]); }
  }, [shareId]);

  useEffect(() => {
    if (data) {
      setLikes(data.likes || 0);
      setComments(data.comments || []);
      setLiked(localStorage.getItem(`mylook_liked_${shareId}`) === "1");
    }
  }, [data, shareId]);

  const myRepliedComments = comments.filter((c) => c.id && myIds.includes(c.id) && c.reply);
  const unseenReplies = myRepliedComments.filter((c) => !seenReplies.includes(c.id));

  const markRepliesSeen = () => {
    const ids = myRepliedComments.map((c) => c.id);
    localStorage.setItem(`mylook_seenreplies_${shareId}`, JSON.stringify(ids));
    setSeenReplies(ids);
    document.getElementById("shared-comments")?.scrollIntoView({ behavior: "smooth" });
  };

  const submitComment = async () => {
    if (!cText.trim()) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/public/outfits/${shareId}/comment`, { author: cAuthor, text: cText });
      setComments((c) => [...c, r.data.comment]);
      setCText("");
      const cid = r.data.comment?.id;
      if (cid) {
        const next = Array.from(new Set([...myIds, cid]));
        localStorage.setItem(`mylook_mine_${shareId}`, JSON.stringify(next));
        setMyIds(next);
      }
    } catch {
      /* ignore */
    } finally {
      setSending(false);
    }
  };

  const doLike = async () => {
    if (liked) return;
    try {
      const r = await axios.post(`${API}/public/outfits/${shareId}/like`);
      setLikes(r.data.likes);
      setLiked(true);
      localStorage.setItem(`mylook_liked_${shareId}`, "1");
    } catch {
      /* ignore */
    }
  };

  if (data === undefined)
    return (
      <div className="min-h-[70vh] grid place-items-center">
        <Loader2 className="h-8 w-8 text-[#2f74f0] animate-spin" />
      </div>
    );

  if (data === null)
    return (
      <div className="min-h-[70vh] grid place-items-center px-4 text-center">
        <div>
          <Shirt className="h-12 w-12 text-[#a9772f]/50 mx-auto mb-4" />
          <h1 className="font-serif text-2xl text-[#2e2015]">Outfit non trovato</h1>
          <p className="text-[#6f5c47] mt-2">Il link potrebbe essere scaduto o non valido.</p>
          <Button onClick={() => navigate("/")} className="mt-6 bg-[#1e5fe0] hover:bg-[#2f74f0] text-white rounded-full px-6">
            Vai su My Look
          </Button>
        </div>
      </div>
    );

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs font-mono uppercase tracking-widest text-[#a9772f]">Look condiviso</span>
      </div>
      <h1 data-testid="shared-outfit-title" className="font-serif text-3xl sm:text-4xl font-semibold text-[#2e2015]">{data.title}</h1>
      {data.views >= 10 && (
        <span data-testid="popular-badge" className="inline-flex items-center gap-1 mt-3 px-3 py-1 rounded-full bg-[#a9772f]/15 border border-[#a9772f]/40 text-[#a9772f] text-xs font-semibold">
          <Flame className="h-3.5 w-3.5" /> Look popolare
        </span>
      )}
      {data.summary && <p className="text-[#6f5c47] mt-2 max-w-2xl">{data.summary}</p>}

      {unseenReplies.length > 0 && (
        <button
          data-testid="reply-notification-banner"
          onClick={markRepliesSeen}
          className="mt-5 w-full sm:w-auto flex items-center gap-3 rounded-xl border border-[#a9772f]/40 bg-[#a9772f]/10 px-4 py-3 text-left hover:bg-[#a9772f]/15 transition-colors"
        >
          <span className="grid place-items-center h-9 w-9 rounded-full bg-[#a9772f] text-white shrink-0">
            <BellRing className="h-4 w-4" />
          </span>
          <span>
            <span className="block text-sm font-semibold text-[#8a5f22]">
              {unseenReplies.length === 1 ? "Hai una nuova risposta!" : `Hai ${unseenReplies.length} nuove risposte!`}
            </span>
            <span className="block text-xs text-[#a9772f]">Il proprietario ha risposto al tuo commento · tocca per vederla</span>
          </span>
        </button>
      )}

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="grid lg:grid-cols-12 gap-6 mt-8"
      >
        <div className="lg:col-span-5">
          <div className="rounded-xl border border-[#1e5fe0]/25 bg-white/75 overflow-hidden">
            {data.has_image ? (
              <img
                data-testid="shared-outfit-image"
                src={`${API}/public/outfits/${shareId}/image`}
                alt={data.title}
                className="w-full object-cover"
              />
            ) : (
              <div className="h-96 grid place-items-center"><Shirt className="h-10 w-10 text-[#a9772f]/40" /></div>
            )}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Badge className="bg-[#1e5fe0]/15 text-[#1e5fe0] border-[#1e5fe0]/30">{data.style}</Badge>
            <span className="text-[#2f74f0] font-mono">Totale €{data.total_price}</span>
            <Button
              data-testid="shared-like-btn"
              onClick={doLike}
              disabled={liked}
              variant="outline"
              className={`h-9 bg-transparent ${liked ? "border-red-400/50 text-red-500" : "border-[#1e5fe0]/30 text-[#1a4fc0] hover:bg-[#1e5fe0]/10"}`}
            >
              <Heart className={`mr-2 h-4 w-4 ${liked ? "fill-red-500 text-red-500" : ""}`} />
              {liked ? "Ti piace" : "Mi piace"}{likes > 0 ? ` · ${likes}` : ""}
            </Button>
            {data.has_image && (
              <Button
                data-testid="shared-download-btn"
                variant="outline"
                onClick={async () => {
                  try {
                    const r = await axios.get(`${API}/public/outfits/${shareId}/download`, { responseType: "blob" });
                    const url = URL.createObjectURL(r.data);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `mylook-${(data.title || "look").replace(/\s+/g, "-").toLowerCase()}.png`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    URL.revokeObjectURL(url);
                  } catch { /* ignore */ }
                }}
                className="ml-auto border-[#a9772f]/40 text-[#a9772f] hover:bg-[#a9772f]/10 bg-transparent h-9"
              >
                <Download className="mr-2 h-4 w-4" /> Salva immagine
              </Button>
            )}
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="space-y-3">
            {data.items.map((it, i) => (
              <div key={i} className="rounded-lg border border-[#1e5fe0]/15 bg-white/75 p-4 flex items-center gap-4">
                {it.image ? (
                  <img src={it.image} alt={it.name} className="h-14 w-14 rounded-md object-cover border border-[#1e5fe0]/20 shrink-0 bg-white" />
                ) : (
                  <div className="h-14 w-14 rounded-md border border-[#1e5fe0]/20 shrink-0 bg-[#dce6ed]/60 grid place-items-center text-[#a9772f]"><Shirt className="h-5 w-5" /></div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-mono uppercase tracking-widest text-[#a9772f]">{it.category}</div>
                  <div className="font-medium text-[#2e2015] truncate">{it.name}</div>
                  <div className="text-sm text-[#8a7760]">{it.brand}{it.color ? ` · ${it.color}` : ""}{it.size ? ` · Taglia ${it.size}` : ""}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono text-[#2f74f0]">€{it.price}</div>
                  {it.buy_url && (
                    <a href={it.buy_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#6f5c47] hover:text-[#1e5fe0] mt-1">
                      Vedi <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      <section id="shared-comments" data-testid="shared-comments" className="mt-12">
        <h2 className="font-serif text-2xl text-[#2e2015] flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-[#2f74f0]" /> Commenti degli amici
        </h2>
        <div className="mt-5 rounded-xl border border-[#1e5fe0]/20 bg-white/75 p-5">
          <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-start">
            <div className="space-y-3">
              <Input
                data-testid="comment-author"
                value={cAuthor}
                onChange={(e) => setCAuthor(e.target.value)}
                placeholder="Il tuo nome (facoltativo)"
                className="bg-white border-[#1e5fe0]/20 text-[#2e2015]"
              />
              <Textarea
                data-testid="comment-text"
                value={cText}
                onChange={(e) => setCText(e.target.value)}
                placeholder="Cosa ne pensi di questo look?"
                className="bg-white border-[#1e5fe0]/20 text-[#2e2015] min-h-[80px]"
              />
            </div>
            <Button
              data-testid="comment-submit"
              onClick={submitComment}
              disabled={sending || !cText.trim()}
              className="bg-[#1e5fe0] hover:bg-[#2f74f0] text-white font-semibold h-11 sm:mt-0"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-2 h-4 w-4" /> Invia</>}
            </Button>
          </div>

          <div className="mt-6 space-y-4">
            {comments.length === 0 ? (
              <p className="text-sm text-[#8a7760]">Ancora nessun commento. Scrivi il primo!</p>
            ) : (
              comments.slice().reverse().map((c, i) => (
                <div key={c.id || i} data-testid="comment-item" className="border-b border-[#1e5fe0]/10 pb-3 last:border-0">
                  <div className="flex items-center gap-2">
                    <span className="grid place-items-center h-7 w-7 rounded-full bg-[#1e5fe0]/10 text-[#1e5fe0] text-xs font-bold">
                      {(c.author || "A").charAt(0).toUpperCase()}
                    </span>
                    <span className="text-sm font-medium text-[#3a2a1c]">{c.author || "Anonimo"}</span>
                  </div>
                  <p className="text-sm text-[#4d3a2a] mt-1.5 pl-9">{c.text}</p>
                  {c.id && myIds.includes(c.id) && (
                    <span data-testid="my-comment-tag" className="ml-9 mt-1 inline-block text-[10px] font-mono uppercase tracking-widest text-[#2f74f0]">Il tuo commento</span>
                  )}
                  {c.reply && (
                    <div data-testid="comment-reply" className="mt-2 ml-9 pl-3 border-l-2 border-[#a9772f]/40">
                      <div className="text-xs font-semibold text-[#a9772f]">{c.reply.author || "Proprietario"} · risponde</div>
                      <p className="text-sm text-[#4d3a2a] mt-0.5">{c.reply.text}</p>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      <div className="mt-12 rounded-2xl border border-[#1e5fe0]/25 bg-gradient-to-br from-white/80 to-[#c4d6e4] p-8 text-center">
        <Ruler className="h-6 w-6 text-[#2f74f0] mx-auto mb-3" />
        <h2 className="font-serif text-2xl text-[#2e2015]">Vuoi un look su misura per te?</h2>
        <p className="text-[#6f5c47] mt-2 max-w-xl mx-auto">Carica le tue foto e lascia che l'IA di My Look componga outfit completi sulla tua corporatura.</p>
        <Button
          data-testid="shared-outfit-cta"
          onClick={() => navigate("/auth")}
          className="mt-6 bg-[#1e5fe0] hover:bg-[#2f74f0] text-white font-semibold rounded-full px-8 h-12"
        >
          <Sparkles className="mr-2 h-4 w-4" /> Crea il mio look
        </Button>
      </div>
    </div>
  );
}
